import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Section, Card, Badge, Button, Alert, Input, Label } from "../design-system";
import { useAuth } from "../auth/AuthProvider";

/** Small helper: extract error code from apiPost/apiPatch/apiDelete Error shape. */
function errCode(e) {
  const d = e?.data?.detail;
  if (typeof d === "string") return d;
  if (d && typeof d === "object" && d.code) return d.code;
  return e?.message || "unknown_error";
}

const IT_ERR = {
  no_changes_proposed: "Nessuna modifica proposta rispetto ai dati attuali.",
  pending_request_exists: "Hai già una richiesta in attesa di approvazione.",
  invalid_email: "Indirizzo email non valido.",
  email_already_registered: "Questo indirizzo email è già presente nell'elenco.",
  delivery_point_not_found: "Punto di consegna non trovato.",
  cannot_delete_last_delivery_point: "Non puoi eliminare l'unico punto di consegna. Aggiungine un altro prima.",
  cannot_unset_last_default: "Deve esistere almeno un punto di consegna predefinito.",
  notification_email_not_found: "Email non trovata.",
  customer_only: "Sezione riservata ai clienti.",
};

function toIT(code) {
  return IT_ERR[code] || code;
}

function fmtDT(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("it-IT"); } catch { return iso; }
}

const STATUS_LABEL = {
  PENDING: { label: "In attesa", tone: "warning" },
  APPROVED: { label: "Approvata", tone: "success" },
  REJECTED: { label: "Rifiutata", tone: "danger" },
};

const CHANGEABLE_FIELDS_LABELS = {
  business_name: "Ragione sociale",
  vat_number: "Partita IVA",
  tax_code: "Codice fiscale",
  email: "Email",
  phone: "Telefono",
  legal_address: "Indirizzo sede legale",
  legal_zip_code: "CAP",
  legal_city: "Comune",
  legal_province: "Provincia",
  pec: "PEC",
  sdi_code: "Codice SDI",
};

// ---------------------------- DATI AZIENDA TAB ----------------------------

function CompanyTab({ profile, pending, reload }) {
  const { apiPost } = useAuth();
  const [form, setForm] = useState(() => ({ ...profile }));
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => { setForm({ ...profile }); }, [profile]);

  const dirty = useMemo(() => Object.keys(CHANGEABLE_FIELDS_LABELS)
    .some((k) => (form[k] ?? "") !== (profile[k] ?? "")), [form, profile]);

  const submit = async () => {
    setBusy(true); setMsg(null); setErr(null);
    try {
      // v34.28 — send ONLY fields the user actually changed. Any field left
      // untouched is omitted from the payload; the backend safety layer also
      // normalizes placeholder tokens like "-" so accidental blanking of
      // sensitive anagraphic fields is prevented.
      const payload = { request_notes: notes || null };
      for (const k of Object.keys(CHANGEABLE_FIELDS_LABELS)) {
        const before = profile[k] ?? "";
        const now = form[k] ?? "";
        if (before !== now) {
          payload[k] = now === "" ? null : now;
        }
      }
      const API = process.env.REACT_APP_BACKEND_URL;
      const tok = localStorage.getItem("access_token");
      const r = await fetch(`${API}/api/customer/account/profile`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${tok}` },
        body: JSON.stringify(payload),
      });
      const body = await r.json();
      if (!r.ok) throw { data: body, status: r.status };
      setMsg("Richiesta di modifica inviata. Verrà valutata dall'amministrazione.");
      setNotes("");
      await reload();
    } catch (e) {
      setErr(toIT(errCode(e)));
    } finally { setBusy(false); }
  };

  const field = (k, extraProps = {}) => (
    <div style={{ marginBottom: 10 }}>
      <Label htmlFor={`f-${k}`}>{CHANGEABLE_FIELDS_LABELS[k]}</Label>
      <Input
        id={`f-${k}`}
        data-testid={`account-field-${k}`}
        value={form[k] ?? ""}
        onChange={(e) => setForm({ ...form, [k]: e.target.value })}
        disabled={!!pending}
        {...extraProps}
      />
    </div>
  );

  return (
    <Card>
      <div style={{ padding: 16 }}>
        <Alert tone="info" data-testid="account-approval-hint">
          <strong>Le modifiche ai dati societari saranno sottoposte ad approvazione</strong>{" "}
          dall'amministrazione. Nulla verrà aggiornato in modo diretto.
        </Alert>
        {pending && (
          <Alert tone="warning" data-testid="account-pending-request">
            Hai già una richiesta di modifica in attesa (creata il {fmtDT(pending.created_at)}). Attendine
            l'esito prima di inviarne un'altra.
          </Alert>
        )}
        {msg && <Alert tone="success" data-testid="account-msg-ok">{msg}</Alert>}
        {err && <Alert tone="danger" data-testid="account-msg-err">{err}</Alert>}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 12 }}>
          {field("business_name")}
          {field("vat_number")}
          {field("tax_code")}
          {field("phone")}
          {field("email", { type: "email" })}
          {field("pec", { type: "email" })}
          {field("sdi_code", { maxLength: 20 })}
          <div />
          {field("legal_address")}
          {field("legal_zip_code", { maxLength: 10 })}
          {field("legal_city")}
          {field("legal_province", { maxLength: 4 })}
        </div>

        <div style={{ marginTop: 10 }}>
          <Label htmlFor="req-notes">Note per l'amministrazione (facoltativo)</Label>
          <Input
            id="req-notes"
            data-testid="account-request-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Es. cambio ragione sociale a seguito di fusione"
            disabled={!!pending}
          />
        </div>

        <div style={{ marginTop: 14, display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <Button
            data-testid="account-submit-change-request"
            variant="primary"
            loading={busy} disabled={busy || !dirty || !!pending}
            onClick={submit}
          >
            {busy ? "Invio…" : "Invia richiesta di modifica"}
          </Button>
        </div>
      </div>
    </Card>
  );
}

// ---------------------------- PUNTI CONSEGNA TAB ----------------------------

const DP_EMPTY = {
  name: "", address: "", zip_code: "", city: "", province: "", region: "",
  phone: "", warehouse_contact_name: "", warehouse_contact_phone: "",
  truck_accessible: null, tail_lift_required: null,
  booking_required: null, loading_dock_available: null,
  allowed_days: [],
  delivery_time_from: "", delivery_time_to: "",
  // Virtual fascia inputs — mapped to canonical unloading_time_from/to +
  // break_from/break_to on save. See dpFasceFromForm / dpFasceFromDp below.
  _fa1_from: "", _fa1_to: "", _fa2_from: "", _fa2_to: "",
  access_notes: "", logistic_notes: "", unloading_instructions: "",
  is_default: false,
};

/** Extract Fascia 1/2 inputs from a canonical DP payload (backend shape). */
function dpFasceFromDp(dp) {
  const uf = dp.unloading_time_from || "";
  const ut = dp.unloading_time_to || "";
  const bf = dp.break_from || "";
  const bt = dp.break_to || "";
  const hasSecond = !!bf && !!bt;
  return {
    _fa1_from: uf,
    _fa1_to: hasSecond ? bf : ut,
    _fa2_from: hasSecond ? bt : "",
    _fa2_to: hasSecond ? ut : "",
  };
}

/** Return {payload_patch, error_it} — validates and canonicalises.
 *  Business rule: EITHER slot may be empty. AT LEAST ONE complete fascia.
 *  If only one fascia is filled (regardless of which UI slot), it is stored
 *  canonically in `unloading_time_from → unloading_time_to` with break=null.
 *  If both are filled, they are reordered chronologically before mapping to
 *  the canonical fascia 1 = unloading_from→break_from, fascia 2 = break_to→unloading_to.
 */
function dpFasceToCanonical(form) {
  const f1a = (form._fa1_from || "").trim();
  const f1b = (form._fa1_to || "").trim();
  const f2a = (form._fa2_from || "").trim();
  const f2b = (form._fa2_to || "").trim();
  const fa1_partial = (!!f1a) !== (!!f1b);
  const fa2_partial = (!!f2a) !== (!!f2b);
  if (fa1_partial) return { error_it: "Fascia oraria 1: compila entrambi gli orari o lasciali entrambi vuoti." };
  if (fa2_partial) return { error_it: "Fascia oraria 2: compila entrambi gli orari o lasciali entrambi vuoti." };
  const fa1_set = !!f1a && !!f1b;
  const fa2_set = !!f2a && !!f2b;
  if (!fa1_set && !fa2_set) return { error_it: "Compila almeno una fascia oraria di scarico." };
  if (fa1_set && f1a >= f1b) return { error_it: "Fascia oraria 1: 'Dalle' deve essere precedente ad 'Alle'." };
  if (fa2_set && f2a >= f2b) return { error_it: "Fascia oraria 2: 'Dalle' deve essere precedente ad 'Alle'." };
  if (fa1_set && fa2_set) {
    // Reorder chronologically so canonical save is consistent regardless of
    // which UI slot the user used for morning vs afternoon.
    let a1 = f1a, b1 = f1b, a2 = f2a, b2 = f2b;
    if (a1 > a2) { [a1, b1, a2, b2] = [a2, b2, a1, b1]; }
    if (!(a1 < b1 && b1 < a2 && a2 < b2)) {
      return { error_it: "Le due fasce si sovrappongono o non sono in ordine cronologico." };
    }
    return { payload_patch: {
      unloading_time_from: a1, break_from: b1,
      break_to: a2, unloading_time_to: b2,
    }};
  }
  // Exactly one fascia populated — use whichever slot it is.
  const only_from = fa1_set ? f1a : f2a;
  const only_to = fa1_set ? f1b : f2b;
  return { payload_patch: {
    unloading_time_from: only_from, unloading_time_to: only_to,
    break_from: null, break_to: null,
  }};
}

function DeliveryPointsTab() {
  const { apiGet, apiPost, apiPatch, apiDelete } = useAuth();
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null); // dp obj or "new" or null
  const [form, setForm] = useState(DP_EMPTY);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);

  const load = async () => {
    try {
      const r = await apiGet("/api/customer/account/delivery-points");
      setRows(r.items || []);
    } catch (e) { setErr(toIT(errCode(e))); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const startNew = () => { setEditing("new"); setForm({ ...DP_EMPTY }); setMsg(null); setErr(null); };
  const startEdit = (dp) => {
    setEditing(dp);
    setForm({
      ...DP_EMPTY, ...dp,
      delivery_time_from: dp.delivery_time_from || "",
      delivery_time_to: dp.delivery_time_to || "",
      ...dpFasceFromDp(dp),
    });
    setMsg(null); setErr(null);
  };
  const cancel = () => { setEditing(null); setForm(DP_EMPTY); };

  const save = async () => {
    setBusy(true); setMsg(null); setErr(null);
    try {
      // Validate + canonicalise the two-fascia unloading window mapping.
      const fasce = dpFasceToCanonical(form);
      if (fasce.error_it) { setErr(fasce.error_it); setBusy(false); return; }
      const payload = { ...form, ...fasce.payload_patch };
      // strip virtual fascia keys — backend doesn't know about them
      delete payload._fa1_from; delete payload._fa1_to;
      delete payload._fa2_from; delete payload._fa2_to;
      // strip empty time strings
      for (const k of ["delivery_time_from", "delivery_time_to"]) {
        if (!payload[k]) payload[k] = null;
      }
      if (editing === "new") {
        await apiPost("/api/customer/account/delivery-points", payload);
        setMsg("Punto di consegna creato.");
      } else {
        await apiPatch(`/api/customer/account/delivery-points/${editing.id}`, payload);
        setMsg("Punto di consegna aggiornato.");
      }
      cancel(); await load();
    } catch (e) { setErr(toIT(errCode(e))); }
    finally { setBusy(false); }
  };

  const makeDefault = async (dp) => {
    setBusy(true); setMsg(null); setErr(null);
    try {
      await apiPatch(`/api/customer/account/delivery-points/${dp.id}`, { is_default: true });
      setMsg(`${dp.name} impostata come predefinita.`);
      await load();
    } catch (e) { setErr(toIT(errCode(e))); }
    finally { setBusy(false); }
  };

  const remove = async (dp) => {
    if (!window.confirm(`Rimuovere il punto di consegna "${dp.name}"?`)) return;
    setBusy(true); setMsg(null); setErr(null);
    try {
      await apiDelete(`/api/customer/account/delivery-points/${dp.id}`);
      setMsg("Punto di consegna rimosso.");
      await load();
    } catch (e) { setErr(toIT(errCode(e))); }
    finally { setBusy(false); }
  };

  const fld = (k, label, extra = {}) => (
    <div style={{ marginBottom: 8 }}>
      <Label htmlFor={`dpf-${k}`}>{label}</Label>
      <Input
        id={`dpf-${k}`}
        data-testid={`dp-field-${k}`}
        value={form[k] ?? ""}
        onChange={(e) => setForm({ ...form, [k]: e.target.value })}
        {...extra}
      />
    </div>
  );

  return (
    <div>
      {msg && <Alert tone="success" data-testid="dp-msg-ok">{msg}</Alert>}
      {err && <Alert tone="danger" data-testid="dp-msg-err">{err}</Alert>}

      {editing === null && (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <div className="muted" style={{ fontSize: ".9rem" }}>
              {rows.length} sed{rows.length === 1 ? "e" : "i"} configurate
            </div>
            <Button data-testid="dp-new-btn" variant="primary" onClick={startNew}>+ Nuovo punto consegna</Button>
          </div>
          <div style={{ display: "grid", gap: 10 }}>
            {rows.map((dp) => (
              <Card key={dp.id} data-testid={`dp-row-${dp.id}`}>
                <div style={{ padding: 12, display: "flex", gap: 10, alignItems: "flex-start" }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      <strong>{dp.name}</strong>
                      {dp.is_default && <Badge tone="success" data-testid={`dp-default-badge-${dp.id}`}>Predefinito</Badge>}
                      {dp.truck_accessible === true && <Badge tone="success" data-testid={`dp-bilico-${dp.id}`}>Bilico: SI</Badge>}
                      {dp.truck_accessible === false && <Badge tone="warning" data-testid={`dp-bilico-${dp.id}`}>Bilico: NO</Badge>}
                      {(dp.truck_accessible === null || dp.truck_accessible === undefined) && <Badge tone="neutral" data-testid={`dp-bilico-${dp.id}`}>Bilico: DA VERIFICARE</Badge>}
                      {dp.tail_lift_required === true && <Badge tone="info" data-testid={`dp-sponda-${dp.id}`}>Sponda: SI</Badge>}
                      {dp.tail_lift_required === false && <Badge tone="neutral" data-testid={`dp-sponda-${dp.id}`}>Sponda: NO</Badge>}
                      {(dp.tail_lift_required === null || dp.tail_lift_required === undefined) && <Badge tone="neutral" data-testid={`dp-sponda-${dp.id}`}>Sponda: DA VERIFICARE</Badge>}
                    </div>
                    <div className="muted" style={{ fontSize: ".85rem", marginTop: 4 }}>
                      {dp.address}, {dp.zip_code} {dp.city} ({dp.province}) · {dp.region}
                    </div>
                    {dp.phone && <div className="muted" style={{ fontSize: ".85rem" }}>Tel: {dp.phone}</div>}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {!dp.is_default && (
                      <Button size="sm" variant="outline" data-testid={`dp-make-default-${dp.id}`}
                              loading={busy} disabled={busy} onClick={() => makeDefault(dp)}>Imposta predefinito</Button>
                    )}
                    <Button size="sm" variant="outline" data-testid={`dp-edit-${dp.id}`} onClick={() => startEdit(dp)}>Modifica</Button>
                    <Button size="sm" variant="ghost" data-testid={`dp-delete-${dp.id}`}
                            loading={busy} disabled={busy} onClick={() => remove(dp)}>Rimuovi</Button>
                  </div>
                </div>
              </Card>
            ))}
            {rows.length === 0 && <Card><div style={{ padding: 12 }}>Nessun punto di consegna configurato.</div></Card>}
          </div>
        </>
      )}

      {editing !== null && (
        <Card data-testid="dp-editor">
          <div style={{ padding: 14 }}>
            <h3 style={{ marginTop: 0 }}>{editing === "new" ? "Nuovo punto di consegna" : `Modifica ${editing.name}`}</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              {fld("name", "Nome sede *")}
              {fld("phone", "Telefono")}
              {fld("address", "Indirizzo *")}
              {fld("zip_code", "CAP *", { maxLength: 10 })}
              {fld("city", "Città *")}
              {fld("province", "Provincia *", { maxLength: 4 })}
              {fld("region", "Regione *")}
              {fld("warehouse_contact_name", "Referente magazzino")}
              {fld("warehouse_contact_phone", "Tel. referente")}
            </div>
            <div style={{ marginTop: 12, padding: 10, border: "1px solid #e5e7eb", borderRadius: 6, background: "#f9fafb" }}
                 data-testid="dp-unloading-fasce-block">
              <div style={{ fontWeight: 600, marginBottom: 6 }}>Fasce orarie di scarico</div>
              <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
                Compila almeno una fascia oraria. Puoi indicare una sola fascia oppure due.
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, alignItems: "end" }}>
                <div>
                  <Label htmlFor="dpf-_fa1_from">Fascia oraria 1 — Dalle</Label>
                  <Input id="dpf-_fa1_from" type="time" data-testid="dp-field-fa1-from"
                         value={form._fa1_from ?? ""}
                         onChange={(e) => setForm({ ...form, _fa1_from: e.target.value })} />
                </div>
                <div>
                  <Label htmlFor="dpf-_fa1_to">Fascia oraria 1 — Alle</Label>
                  <Input id="dpf-_fa1_to" type="time" data-testid="dp-field-fa1-to"
                         value={form._fa1_to ?? ""}
                         onChange={(e) => setForm({ ...form, _fa1_to: e.target.value })} />
                </div>
                <div>
                  <Label htmlFor="dpf-_fa2_from">Fascia oraria 2 — Dalle</Label>
                  <Input id="dpf-_fa2_from" type="time" data-testid="dp-field-fa2-from"
                         value={form._fa2_from ?? ""}
                         onChange={(e) => setForm({ ...form, _fa2_from: e.target.value })} />
                </div>
                <div>
                  <Label htmlFor="dpf-_fa2_to">Fascia oraria 2 — Alle</Label>
                  <Input id="dpf-_fa2_to" type="time" data-testid="dp-field-fa2-to"
                         value={form._fa2_to ?? ""}
                         onChange={(e) => setForm({ ...form, _fa2_to: e.target.value })} />
                </div>
              </div>
              <div style={{ marginTop: 6, display: "flex", gap: 8 }}>
                {(form._fa1_from || form._fa1_to) && (
                  <Button size="sm" variant="ghost" data-testid="dp-fa1-clear-btn"
                          onClick={() => setForm({ ...form, _fa1_from: "", _fa1_to: "" })}>
                    Svuota Fascia 1
                  </Button>
                )}
                {(form._fa2_from || form._fa2_to) && (
                  <Button size="sm" variant="ghost" data-testid="dp-fa2-clear-btn"
                          onClick={() => setForm({ ...form, _fa2_from: "", _fa2_to: "" })}>
                    Svuota Fascia 2
                  </Button>
                )}
              </div>
            </div>
            <div style={{ marginTop: 10 }}>
              <Label>Giorni di scarico / ricevimento</Label>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}
                   data-testid="dp-field-allowed_days">
                {[["Lun",0],["Mar",1],["Mer",2],["Gio",3],["Ven",4],["Sab",5],["Dom",6]].map(([lbl,idx]) => {
                  const arr = Array.isArray(form.allowed_days) ? form.allowed_days : [];
                  const on = arr.includes(idx);
                  return (
                    <label key={idx} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <input type="checkbox" checked={on}
                             data-testid={`dp-allowed_day-${idx}`}
                             onChange={(e) => {
                               const next = e.target.checked
                                 ? [...arr, idx].sort((a,b)=>a-b)
                                 : arr.filter((x) => x !== idx);
                               setForm({ ...form, allowed_days: next });
                             }} />
                      {lbl}
                    </label>
                  );
                })}
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 10 }}
                 data-testid="dp-trilobean-group">
              <div>
                <Label>Accesso consentito a bilico / autoarticolato?</Label>
                <select className="ds-input" data-testid="dp-field-truck_accessible"
                        value={form.truck_accessible === true ? "yes" : form.truck_accessible === false ? "no" : ""}
                        onChange={(e) => setForm({ ...form,
                            truck_accessible: e.target.value === "yes" ? true : e.target.value === "no" ? false : null })}>
                  <option value="">— Da verificare —</option>
                  <option value="yes">Sì</option>
                  <option value="no">No</option>
                </select>
              </div>
              <div>
                <Label>Sponda idraulica necessaria?</Label>
                <select className="ds-input" data-testid="dp-field-tail_lift_required"
                        value={form.tail_lift_required === true ? "yes" : form.tail_lift_required === false ? "no" : ""}
                        onChange={(e) => setForm({ ...form,
                            tail_lift_required: e.target.value === "yes" ? true : e.target.value === "no" ? false : null })}>
                  <option value="">— Da verificare —</option>
                  <option value="yes">Sì</option>
                  <option value="no">No</option>
                </select>
              </div>
              <div>
                <Label>Prenotazione scarico obbligatoria?</Label>
                <select className="ds-input" data-testid="dp-field-booking_required"
                        value={form.booking_required === true ? "yes" : form.booking_required === false ? "no" : ""}
                        onChange={(e) => setForm({ ...form,
                            booking_required: e.target.value === "yes" ? true : e.target.value === "no" ? false : null })}>
                  <option value="">— seleziona —</option>
                  <option value="yes">Sì</option>
                  <option value="no">No</option>
                </select>
              </div>
              <div>
                <Label>Banchina / rampa di scarico disponibile?</Label>
                <select className="ds-input" data-testid="dp-field-loading_dock_available"
                        value={form.loading_dock_available === true ? "yes" : form.loading_dock_available === false ? "no" : ""}
                        onChange={(e) => setForm({ ...form,
                            loading_dock_available: e.target.value === "yes" ? true : e.target.value === "no" ? false : null })}>
                  <option value="">— seleziona —</option>
                  <option value="yes">Sì</option>
                  <option value="no">No</option>
                </select>
              </div>
            </div>
            <div style={{ display: "flex", gap: 16, marginTop: 8 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <input type="checkbox" data-testid="dp-field-is_default"
                       checked={!!form.is_default}
                       onChange={(e) => setForm({ ...form, is_default: e.target.checked })} />
                Imposta come predefinito
              </label>
            </div>
            <div style={{ marginTop: 10 }}>
              <Label htmlFor="dpf-access_notes">Note accesso</Label>
              <Input id="dpf-access_notes" data-testid="dp-field-access_notes"
                     value={form.access_notes ?? ""} onChange={(e) => setForm({ ...form, access_notes: e.target.value })} />
            </div>
            <div style={{ marginTop: 10 }}>
              <Label htmlFor="dpf-logistic_notes">Note logistiche</Label>
              <Input id="dpf-logistic_notes" data-testid="dp-field-logistic_notes"
                     value={form.logistic_notes ?? ""} onChange={(e) => setForm({ ...form, logistic_notes: e.target.value })} />
            </div>
            <div style={{ marginTop: 10 }}>
              <Label htmlFor="dpf-unloading_instructions">Istruzioni scarico</Label>
              <Input id="dpf-unloading_instructions" data-testid="dp-field-unloading_instructions"
                     value={form.unloading_instructions ?? ""} onChange={(e) => setForm({ ...form, unloading_instructions: e.target.value })} />
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
              <Button variant="ghost" data-testid="dp-cancel-btn" onClick={cancel} loading={busy} disabled={busy}>Annulla</Button>
              <Button variant="primary" data-testid="dp-save-btn" onClick={save} loading={busy} disabled={busy}>
                {busy ? "Salvataggio…" : "Salva"}
              </Button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}

// ---------------------------- EMAIL NOTIFICHE TAB ----------------------------

function NotificationEmailsTab() {
  const { apiGet, apiPost, apiPatch, apiDelete } = useAuth();
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ email: "", label: "", is_primary: false });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);

  const load = async () => {
    try { const r = await apiGet("/api/customer/account/notification-emails"); setRows(r.items || []); }
    catch (e) { setErr(toIT(errCode(e))); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const add = async () => {
    setBusy(true); setMsg(null); setErr(null);
    try {
      await apiPost("/api/customer/account/notification-emails", { ...form, is_active: true });
      setMsg("Email aggiunta.");
      setForm({ email: "", label: "", is_primary: false });
      await load();
    } catch (e) { setErr(toIT(errCode(e))); }
    finally { setBusy(false); }
  };

  const toggleActive = async (row) => {
    setBusy(true); setMsg(null); setErr(null);
    try {
      await apiPatch(`/api/customer/account/notification-emails/${row.id}`, { is_active: !row.is_active });
      await load();
    } catch (e) { setErr(toIT(errCode(e))); }
    finally { setBusy(false); }
  };
  const makePrimary = async (row) => {
    setBusy(true); setMsg(null); setErr(null);
    try {
      await apiPatch(`/api/customer/account/notification-emails/${row.id}`, { is_primary: true });
      await load();
    } catch (e) { setErr(toIT(errCode(e))); }
    finally { setBusy(false); }
  };
  const remove = async (row) => {
    if (!window.confirm(`Rimuovere ${row.email}?`)) return;
    setBusy(true); setMsg(null); setErr(null);
    try {
      await apiDelete(`/api/customer/account/notification-emails/${row.id}`);
      setMsg("Email rimossa.");
      await load();
    } catch (e) { setErr(toIT(errCode(e))); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <Alert tone="info">
        Questi indirizzi riceveranno le notifiche operative del portale (conferma ordine, spedizione, consegna, ecc.).
        L'email principale è il destinatario TO; le altre attive vanno in CC.
      </Alert>
      {msg && <Alert tone="success" data-testid="nem-msg-ok">{msg}</Alert>}
      {err && <Alert tone="danger" data-testid="nem-msg-err">{err}</Alert>}

      <Card>
        <div style={{ padding: 14 }}>
          <h4 style={{ marginTop: 0 }}>Aggiungi email</h4>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr auto", gap: 10, alignItems: "end" }}>
            <div>
              <Label htmlFor="nem-email">Indirizzo email *</Label>
              <Input id="nem-email" data-testid="nem-field-email" type="email"
                     value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                     placeholder="ordini@azienda.it" />
            </div>
            <div>
              <Label htmlFor="nem-label">Etichetta</Label>
              <Input id="nem-label" data-testid="nem-field-label"
                     value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
                     placeholder="Es. Ufficio acquisti" />
            </div>
            <Button data-testid="nem-add-btn" variant="primary" onClick={add} loading={busy} disabled={busy || !form.email}>
              {busy ? "…" : "Aggiungi"}
            </Button>
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8 }}>
            <input type="checkbox" data-testid="nem-field-is_primary"
                   checked={!!form.is_primary} onChange={(e) => setForm({ ...form, is_primary: e.target.checked })} />
            Imposta come email principale (TO)
          </label>
        </div>
      </Card>

      <div style={{ display: "grid", gap: 8, marginTop: 14 }}>
        {rows.map((r) => (
          <Card key={r.id} data-testid={`nem-row-${r.id}`}>
            <div style={{ padding: 12, display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                  <strong>{r.email}</strong>
                  {r.is_primary && <Badge tone="success">Principale</Badge>}
                  {!r.is_active && <Badge tone="neutral">Disattivata</Badge>}
                </div>
                {r.label && <div className="muted" style={{ fontSize: ".85rem" }}>{r.label}</div>}
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                {!r.is_primary && r.is_active && (
                  <Button size="sm" variant="outline" data-testid={`nem-make-primary-${r.id}`}
                          onClick={() => makePrimary(r)} loading={busy} disabled={busy}>Principale</Button>
                )}
                <Button size="sm" variant="outline" data-testid={`nem-toggle-active-${r.id}`}
                        onClick={() => toggleActive(r)} loading={busy} disabled={busy}>
                  {r.is_active ? "Disattiva" : "Riattiva"}
                </Button>
                <Button size="sm" variant="ghost" data-testid={`nem-delete-${r.id}`}
                        onClick={() => remove(r)} loading={busy} disabled={busy}>Rimuovi</Button>
              </div>
            </div>
          </Card>
        ))}
        {rows.length === 0 && (
          <Card><div style={{ padding: 12 }}>Nessuna email operativa configurata. Le notifiche useranno l'email principale del cliente.</div></Card>
        )}
      </div>
    </div>
  );
}

// ---------------------------- RICHIESTE IN CORSO TAB ----------------------------

function ChangeRequestsTab() {
  const { apiGet } = useAuth();
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState(null);

  useEffect(() => {
    apiGet("/api/customer/account/change-requests")
      .then((r) => setRows(r.items || []))
      .catch((e) => setErr(toIT(errCode(e))));
  }, [apiGet]);

  const fmtChanges = (changes) => {
    if (!changes || typeof changes !== "object") return "—";
    return Object.entries(changes)
      .map(([k, v]) => `${CHANGEABLE_FIELDS_LABELS[k] || k}: ${v ?? "(vuoto)"}`)
      .join(" · ");
  };

  return (
    <div>
      {err && <Alert tone="danger">{err}</Alert>}
      {rows.length === 0 && !err && (
        <Card><div style={{ padding: 12 }}>Nessuna richiesta di modifica registrata.</div></Card>
      )}
      <div style={{ display: "grid", gap: 8 }}>
        {rows.map((r) => {
          const st = STATUS_LABEL[r.status] || { label: r.status, tone: "neutral" };
          return (
            <Card key={r.id} data-testid={`req-row-${r.id}`}>
              <div style={{ padding: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                  <div>
                    <div className="muted" style={{ fontSize: ".8rem" }}>Inviata il {fmtDT(r.created_at)}</div>
                    <div style={{ marginTop: 4, fontSize: ".9rem" }}>{fmtChanges(r.proposed_changes)}</div>
                    {r.request_notes && (
                      <div className="muted" style={{ fontSize: ".8rem", marginTop: 4 }}>
                        Nota cliente: {r.request_notes}
                      </div>
                    )}
                    {r.review_notes && (
                      <div className="muted" style={{ fontSize: ".8rem", marginTop: 4 }}>
                        Risposta amministrazione: {r.review_notes}
                      </div>
                    )}
                  </div>
                  <Badge tone={st.tone} data-testid={`req-status-${r.id}`}>{st.label}</Badge>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------- MAIN PAGE ----------------------------

const TABS = [
  { key: "company", label: "Dati Azienda", testid: "tab-company" },
  { key: "delivery", label: "Punti Consegna", testid: "tab-delivery" },
  { key: "emails", label: "Email Notifiche", testid: "tab-emails" },
  { key: "requests", label: "Richieste in corso", testid: "tab-requests" },
];

export default function CustomerAccountPage() {
  const { apiGet, apiPost } = useAuth();
  const [profile, setProfile] = useState(null);
  const [pending, setPending] = useState(null);
  const [verification, setVerification] = useState({ verified_at: null, required: false });
  const [verifyBusy, setVerifyBusy] = useState(false);
  const [verifyErr, setVerifyErr] = useState(null);
  const [tab, setTab] = useState("company");
  const [err, setErr] = useState(null);
  // v43.35.8 — scroll the tab bar into view when switching from onboarding
  // CTAs. Without this the "Vai ai dati logistici" button visually appears
  // to do nothing because the tab content is rendered below the tall
  // profile-verification card and the viewport stays put.
  const tabsRef = useRef(null);
  const gotoTab = (k) => {
    setTab(k);
    // Defer to next frame so the target tab has actually mounted before we
    // scroll — otherwise on slow renders the scroll target height is stale.
    requestAnimationFrame(() => {
      try {
        tabsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      } catch (_) { /* jsdom/older browsers */ }
    });
  };
  // v43.34 — proactive completeness state (populated on load + after DP save).
  const [completeness, setCompleteness] = useState(null);

  const load = async () => {
    try {
      const r = await apiGet("/api/customer/account/profile");
      setProfile(r.profile);
      setPending(r.pending_change_request);
      setVerification({
        verified_at: r.profile_verified_at || null,
        required: !!r.profile_verification_required,
      });
    } catch (e) { setErr(toIT(errCode(e))); }
    // v43.34 — proactively fetch completeness so we can show the missing
    // logistics fields BEFORE the user clicks "CONFERMO". Silent on failure
    // (the verify POST still gates on the same rules).
    try {
      const c = await apiGet("/api/customer/account/profile/completeness");
      setCompleteness(c);
    } catch (e) { /* non-blocking */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const [verifyMissing, setVerifyMissing] = useState(null);   // list of missing logistics fields, if 422
  const [verifyChecked, setVerifyChecked] = useState(false);
  const confirmVerify = async () => {
    setVerifyBusy(true); setVerifyErr(null); setVerifyMissing(null);
    try {
      await apiPost("/api/customer/account/profile/verify", {});
      setVerifyChecked(false);
      await load();
    } catch (e) {
      // Backend logistics-completeness gate: HTTP 422 with structured detail.
      const d = e?.data?.detail || e?.detail || null;
      if (d && d.code === "CUSTOMER_LOGISTICS_PROFILE_INCOMPLETE") {
        setVerifyMissing(Array.isArray(d.missing_fields) ? d.missing_fields : []);
        setVerifyErr(
          "Completa i dati logistici del punto di consegna prima di confermare il profilo."
        );
      } else {
        setVerifyErr(toIT(errCode(e)));
      }
    }
    finally { setVerifyBusy(false); }
  };

  const MISSING_LABELS = {
    delivery_point: "Punto di consegna (aggiungi almeno un indirizzo di scarico)",
    address: "Indirizzo di scarico",
    zip_code: "CAP",
    city: "Città",
    province: "Provincia",
    warehouse_contact_name: "Referente magazzino",
    warehouse_contact_phone: "Telefono referente magazzino",
    allowed_days: "Giorni di scarico / ricevimento",
    unloading_time_from: "Orario scarico dalle (Fascia 1)",
    unloading_time_to: "Orario scarico alle (Fascia 1)",
    unloading_window_invalid_range: "Fascia 1: 'Dalle' deve essere precedente ad 'Alle'",
    second_window_partial: "Fascia 2 incompleta: compila entrambi gli orari o lascia entrambi vuoti",
    second_window_invalid_range: "Le fasce di scarico si sovrappongono o non sono in ordine",
    truck_accessible: "Accesso consentito a bilico / autoarticolato (Sì/No)",
    tail_lift_required: "Sponda idraulica necessaria (Sì/No)",
    booking_required: "Prenotazione scarico obbligatoria? (Sì/No)",
    loading_dock_available: "Banchina / rampa disponibile? (Sì/No)",
  };

  const fmtField = (v) => (v && String(v).trim() !== "") ? String(v) : "—";

  return (
    <Section
      title="Il mio account"
      subtitle="Gestisci i dati aziendali, i punti di consegna e le email operative"
      actions={<Link to="/customer" data-testid="account-back-home"><Button variant="ghost" size="sm">← Home</Button></Link>}
    >
      {err && <Alert tone="danger">{err}</Alert>}

      {/* v43.34 — Onboarding progress panel — visible whenever profile is
          not yet verified. Shows the 3 canonical steps with ✅ / ⚠️ from the
          same server-side completeness rules used by /profile/verify. */}
      {verification.required && (
        <Card data-testid="onboarding-progress-panel"
              style={{ background: "#F1F5F9", marginBottom: 12,
                       border: "1px solid #CBD5E1" }}>
          <div style={{ padding: 14 }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>
              Completa la tua anagrafica
            </div>
            <div style={{ fontSize: ".9rem", color: "#475569", marginBottom: 10 }}>
              Prima di utilizzare tutte le funzioni del portale, completa e
              conferma i dati richiesti.
            </div>
            <div style={{ display: "grid",
                          gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))",
                          gap: 10 }}>
              <div data-testid="progress-step-company"
                   style={{ background: "#fff", padding: 10, borderRadius: 6,
                            border: "1px solid #E2E8F0" }}>
                <div style={{ fontSize: ".8rem", color: "#64748B" }}>1. Dati aziendali</div>
                <div style={{ fontWeight: 700 }}>
                  {pending ? "⏳ In verifica GE" : "⚠️ Da confermare"}
                </div>
              </div>
              <div data-testid="progress-step-logistics"
                   style={{ background: "#fff", padding: 10, borderRadius: 6,
                            border: "1px solid #E2E8F0" }}>
                <div style={{ fontSize: ".8rem", color: "#64748B" }}>2. Dati logistici</div>
                <div style={{ fontWeight: 700 }}>
                  {completeness
                    ? (completeness.logistics_complete ? "✅ Completi" : "⚠️ Da completare")
                    : "…"}
                </div>
                {completeness && !completeness.logistics_complete
                   && (completeness.logistics_missing_fields || []).length > 0 && (
                  <div style={{ fontSize: ".8rem", color: "#B91C1C", marginTop: 4 }}>
                    Mancano {completeness.logistics_missing_fields.length} camp{completeness.logistics_missing_fields.length === 1 ? "o" : "i"}
                  </div>
                )}
              </div>
              <div data-testid="progress-step-final"
                   style={{ background: "#fff", padding: 10, borderRadius: 6,
                            border: "1px solid #E2E8F0" }}>
                <div style={{ fontSize: ".8rem", color: "#64748B" }}>3. Conferma finale</div>
                <div style={{ fontWeight: 700 }}>
                  {completeness && completeness.final_confirm_available
                    ? "✅ Pronto per conferma"
                    : "⚠️ Non ancora disponibile"}
                </div>
              </div>
            </div>
            {completeness && !completeness.logistics_complete
               && (completeness.logistics_missing_fields || []).length > 0 && (
              <div data-testid="onboarding-missing-list-inline"
                   style={{ background: "#FEF3C7", border: "1px solid #F59E0B",
                            borderRadius: 6, padding: 10, marginTop: 12 }}>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>
                  Per completare l'anagrafica mancano:
                </div>
                <ul style={{ margin: "4px 0 8px 20px" }}>
                  {completeness.logistics_missing_fields.map((f) => (
                    <li key={f} data-testid={`onboarding-missing-${f}`}>
                      {MISSING_LABELS[f] || f}
                    </li>
                  ))}
                </ul>
                <Button variant="primary" size="sm"
                        data-testid="onboarding-goto-delivery"
                        onClick={() => gotoTab("delivery")}>
                  Vai ai dati logistici
                </Button>
              </div>
            )}
            {completeness && completeness.logistics_complete && !completeness.profile_verified && (
              <div data-testid="onboarding-ready-to-confirm"
                   style={{ background: "#DCFCE7", border: "1px solid #16A34A",
                            borderRadius: 6, padding: 10, marginTop: 12 }}>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>
                  ✅ Tutti i dati logistici sono completi
                </div>
                <div style={{ fontSize: ".92rem", marginBottom: 8 }}>
                  Manca solo la conferma finale nella scheda <em>Dati Azienda</em>.
                </div>
                {tab !== "company" && (
                  <Button variant="primary" size="sm"
                          data-testid="onboarding-goto-company"
                          onClick={() => gotoTab("company")}>
                    Vai a Dati Azienda per confermare
                  </Button>
                )}
              </div>
            )}
          </div>
        </Card>
      )}

      {verification.required && (
        <Card data-testid="profile-verification-section"
              style={{ border: "2px solid #B45309", background: "#FFFBEB", marginBottom: 16 }}>
          <div style={{ padding: 18 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <Badge tone="warning" data-testid="profile-verification-badge">Verifica anagrafica richiesta</Badge>
            </div>
            <h3 style={{ margin: "0 0 6px", color: "#7C2D12" }}>Conferma i tuoi dati aziendali</h3>
            <p style={{ margin: "0 0 12px" }}>
              Prima di poter inviare un ordine devi completare due passaggi:
              <br /><strong>1) Controlla i dati aziendali</strong> (scheda <em>Dati Azienda</em>).
              Se qualcosa non è corretto, invia una richiesta di modifica: l'ufficio la approverà.
              <br /><strong>2) Completa i dati logistici</strong> del tuo punto di consegna (scheda <em>Punti Consegna</em>):
              referente magazzino, giorni e orari di scarico, accesso bilico, sponda idraulica, prenotazione, banchina.
              I dati logistici sono efficaci subito, senza approvazione.
            </p>
            {verifyErr && (
              <div data-testid="profile-verify-error" style={{
                background: "#fee2e2", border: "1px solid #dc2626", color: "#7f1d1d",
                padding: 10, borderRadius: 6, margin: "10px 0",
              }}>
                <div><strong>{verifyErr}</strong></div>
                {Array.isArray(verifyMissing) && verifyMissing.length > 0 && (
                  <>
                    <div style={{ marginTop: 6 }}>Campi mancanti:</div>
                    <ul style={{ margin: "4px 0 8px 20px" }} data-testid="profile-verify-missing-list">
                      {verifyMissing.map((f) => (
                        <li key={f} data-testid={`profile-verify-missing-${f}`}>
                          {MISSING_LABELS[f] || f}
                        </li>
                      ))}
                    </ul>
                    <Button variant="primary" size="sm"
                            data-testid="profile-verify-goto-delivery"
                            onClick={() => { setVerifyErr(null); setVerifyMissing(null); gotoTab("delivery"); }}>
                      Completa dati logistici
                    </Button>
                  </>
                )}
              </div>
            )}

            {profile && (
              <div data-testid="profile-verification-summary"
                   style={{ background: "#fff", border: "1px solid #FDE68A",
                            borderRadius: 8, padding: 12, marginBottom: 12 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 16px", fontSize: ".92rem" }}>
                  <div><span className="muted">Ragione sociale:</span> <strong>{fmtField(profile.business_name)}</strong></div>
                  <div><span className="muted">Codice cliente:</span> <strong>{fmtField(profile.code)}</strong></div>
                  <div><span className="muted">Partita IVA:</span> <strong>{fmtField(profile.vat_number)}</strong></div>
                  <div><span className="muted">Codice fiscale:</span> <strong>{fmtField(profile.tax_code)}</strong></div>
                  <div><span className="muted">Email:</span> <strong>{fmtField(profile.email)}</strong></div>
                  <div><span className="muted">Telefono:</span> <strong>{fmtField(profile.phone)}</strong></div>
                  <div style={{ gridColumn: "span 2" }}>
                    <span className="muted">Sede legale:</span>{" "}
                    <strong>
                      {fmtField(profile.legal_address)}
                      {profile.legal_zip_code || profile.legal_city ? `, ${fmtField(profile.legal_zip_code)} ${fmtField(profile.legal_city)}` : ""}
                      {profile.legal_province ? ` (${profile.legal_province})` : ""}
                    </strong>
                  </div>
                  <div><span className="muted">PEC:</span> <strong>{fmtField(profile.pec)}</strong></div>
                  <div><span className="muted">Codice SDI:</span> <strong>{fmtField(profile.sdi_code)}</strong></div>
                </div>
              </div>
            )}

            {pending ? (
              <Alert tone="info" data-testid="profile-verify-pending-msg">
                Hai una richiesta di modifica in attesa. La conferma sarà disponibile dopo l'approvazione dell'amministrazione.
              </Alert>
            ) : (
              <>
                <label style={{ display: "flex", alignItems: "center", gap: 8,
                                cursor: "pointer", padding: "8px 0" }}
                       data-testid="profile-verify-checkbox-label">
                  <input
                    type="checkbox"
                    data-testid="profile-verify-checkbox"
                    checked={verifyChecked}
                    onChange={(e) => setVerifyChecked(e.target.checked)}
                  />
                  <span>Ho controllato i dati sopra riportati.</span>
                </label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 4 }}>
                  <Button
                    data-testid="profile-verify-confirm-btn"
                    variant="primary"
                    onClick={confirmVerify}
                    disabled={verifyBusy || !verifyChecked}
                  >
                    {verifyBusy ? "Conferma in corso…" : "CONFERMO CHE I DATI SONO CORRETTI"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setTab("company")}
                          data-testid="profile-verify-open-request">
                    Invia richiesta di modifica
                  </Button>
                </div>
                {verifyErr && <div style={{ marginTop: 8, color: "#b91c1c" }}>{verifyErr}</div>}
              </>
            )}
          </div>
        </Card>
      )}

      {!verification.required && verification.verified_at && (
        <Alert tone="success" data-testid="profile-verification-ok">
          Dati anagrafici confermati come corretti.
        </Alert>
      )}

      <div className="tab-bar" data-testid="account-tabs"
           ref={tabsRef}
           style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14, borderBottom: "1px solid #e5e7eb", paddingBottom: 8, scrollMarginTop: 12 }}>
        {TABS.map((t) => (
          <Button
            key={t.key}
            data-testid={`account-${t.testid}`}
            size="sm"
            variant={tab === t.key ? "primary" : "ghost"}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </Button>
        ))}
      </div>

      {!profile && !err && <Card><div style={{ padding: 12 }}>Caricamento…</div></Card>}

      {profile && tab === "company" && (
        <CompanyTab profile={profile} pending={pending} reload={load} />
      )}
      {profile && tab === "delivery" && <DeliveryPointsTab />}
      {profile && tab === "emails" && <NotificationEmailsTab />}
      {profile && tab === "requests" && <ChangeRequestsTab />}
    </Section>
  );
}
